import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';

import InputGroup from '../../../../../../../components/global/InputGroup';
import useClickOutside from '../../../../../../../hooks/useClickOutside';
import Button from '../../../../../../../components/global/Button';
import Label from '../../../../../../../components/global/Label';
import UploadIcon from '../../../../../../../components/icons/UploadIcon';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { fileInputAccept } from '../../../../../../../constants/photo-upload';
import { getMaxThumb } from '../../../../../../../util/photo';
import PhotoUploadCache from '../../../../../../../components/product/PhotoUploadCache';
import CustomControl from '../../../../../../../components/global/CustomControl';
import Spinner from '../../../../../../../components/global/Spinner';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';

const propTypes = {
  visible: PropTypes.bool.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  onBackgroundColorChange: PropTypes.func.isRequired,
  backgroundImage: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
  }),
  onBackgroundImageChange: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

const SecondaryBackgroundView = ({
  visible,
  backgroundColor,
  onBackgroundColorChange,
  backgroundImage,
  onBackgroundImageChange,
  saving,
}) => {
  const bgColorRef = useRef(null);
  const bgImageRef = useRef(null);
  const [currentColor, setCurrentColor] = useState(backgroundColor);
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [saveImageAsDefault, setSaveImageAsDefault] = useState(false);
  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      await onBackgroundImageChange(assetImage);
    },
  });

  useEffect(() => {
    setCurrentColor(backgroundColor);
  }, [backgroundColor]);

  const handleClick = () => {
    setDisplayColorPicker(prev => !prev);
  };

  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  useClickOutside(bgColorRef, () => {
    handleClose();
  });

  if (!visible) {
    return null;
  }

  return (
    <div className="secondary-background-view-container">
      <InputGroup
        id="background-color"
        value={currentColor}
        label="Background Color"
        wrapperRef={bgColorRef}
        wrapperClassName="slideshow-designer-color-control"
        onClick={handleClick}
        readOnly
        addon={
          <Fragment>
            <div
              aria-hidden
              role="button"
              className="slideshow-designer-color-control-addon"
              style={{ backgroundColor: currentColor }}
              onClick={handleClick}
            />
            {displayColorPicker ? (
              <div className="slideshow-designer-color-control-popover">
                <ChromePicker
                  color={currentColor}
                  onChange={value => setCurrentColor(value.hex)}
                  onChangeComplete={value => {
                    onBackgroundColorChange?.(value.hex);
                  }}
                  width="100%"
                  disableAlpha
                />
              </div>
            ) : null}
          </Fragment>
        }
      />
      <Label id="background-image" className="d-none d-md-block">
        Background Image
      </Label>
      <Button
        btnStyle="info"
        btnBlock
        className="background-button text-primary d-flex align-items-center text-bold mb-md-2"
        disabled={uploading || saving}
        onClick={() => bgImageRef.current.click()}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {uploading ? (
          <Fragment>
            <Spinner className="d-block d-md-none" />
            <span className="d-none d-md-inline-block">Uploading...</span>
          </Fragment>
        ) : saving ? (
          <Fragment>Saving...</Fragment>
        ) : (
          <Fragment>
            <UploadIcon className="mr-md-1" color="#0d84fe" size={18} />
            <span className="d-none d-md-inline-block">Upload an image</span>
          </Fragment>
        )}
      </Button>
      <input
        id="background-image"
        className="secondary-view-background-image-control"
        ref={bgImageRef}
        type="file"
        accept={fileInputAccept}
        onChange={event => {
          const [file] = event.target.files;
          onUpload(file);
        }}
        disabled={uploading}
        value=""
      />
      {backgroundImage && (
        <Fragment>
          <PhotoUploadCache
            id={backgroundImage?.id}
            thumbs={[
              {
                size_code: IMAGE_SIZE_CODE.LARGE2X,
                url: backgroundImage.url,
                width: 1,
                height: 1,
              },
            ]}
            render={thumbs => (
              <div className="background-button secondary-background-view-image-preview">
                <img src={getMaxThumb(thumbs).url} alt="" />
                <Button
                  btnStyle="link"
                  onClick={async () => {
                    await onBackgroundImageChange(null);
                    onUploadReset();
                  }}
                  disabled={saving}
                  className="text-danger"
                >
                  &times;
                </Button>
              </div>
            )}
          />
          {false && (
            <CustomControl
              id="default-background-image"
              label="Save this background as my default for all slideshows"
              labelNote="This background image will be used for all your slideshows in your gallery unless changed."
              type="checkbox"
              checked={saveImageAsDefault}
              onChange={() => setSaveImageAsDefault(prev => !prev)}
              readOnly
            />
          )}
        </Fragment>
      )}
    </div>
  );
};

SecondaryBackgroundView.propTypes = propTypes;

export default SecondaryBackgroundView;
